<template>
  <v-sheet
    color="white"
    elevation="5"
    rounded
    class="mt-5 mb-5 pt-3 pb-5 pl-3 pr-3"
    height="100%"
  >
    <v-overlay :value="loading">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="70"
        :width="7"
      ></v-progress-circular>
    </v-overlay>
    <div>       
      <v-row class="ma-0 mb-3">
        <h2>{{title}}</h2>
        <v-btn class="ml-2" color="primary" v-on:click="createNewLease">+ New Lease</v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" v-on:click="applyFilter">Apply Filters</v-btn>&nbsp;
        <v-btn color="secondary" v-on:click="clearAllFilters">Clear</v-btn>
      </v-row>
    </div>
    <PaginationSelect
      :totalCount="paginatedResponse.totalCount"
      :page="options.page"
      :size="options.itemsPerPage"
      v-on:load="load"
      v-on:changePage="options.page = $event"
      v-on:changeSize="options.itemsPerPage = $event"
    />
    <v-row>
      <v-alert width="100%" class="mr-5 ml-5" v-model="isError" dismissible type="error"> {{ errorMessage }}</v-alert>
    </v-row>
    <v-data-table
      dense
      disable-sort
      hide-default-footer
      class="elevation-1"
      :loading="loading"
      :headers="headers"
      :options.sync="options"
      :items="paginatedResponse.data"
      :server-items-length="paginatedResponse.totalCount"
      :footer-props="footerProps">
        <template v-slot:item.registrationDate="{ item }">
          <span v-if="item.registrationDate">
            <localized-date :date="item.registrationDate" />
          </span>
        </template>
        <template v-slot:item.leaseId="{ item }">
          <router-link :to="`/admin/leases/${item.leaseId}`" target="_blank">
            <span>{{ item.leaseId }}</span>
          </router-link>
        </template>
        <template v-slot:item.dogId="{ item }">
          <router-link :to="`/admin/dogs/${encodeURIComponent(item.dogId)}`" target="_blank">
            <span>{{ item.dogName }} - {{ item.dogId }}</span>
          </router-link>
        </template>
        <template v-slot:item.requester="{ item }">
          <span>{{ item.requester.lastName }}, {{ item.requester.firstName }}</span>
        </template>
        <template v-slot:item.startDate="{ item }">
          <localized-date :date="item.startDate" />
        </template>
        <template v-slot:item.endDate="{ item }">
          <localized-date :date="item.endDate" />
        </template>
        <template v-slot:item.lessees="{ item }">
          <CoownersList
            :owners="item.lessees"
            :ownershipType="leaseOwnershipType"
            :hideHeader="true" />
        </template>
        <template v-slot:item.actionButton="{ item }">
            <v-btn
              v-if="!item.isCancelled"
              color="primary"
              class="white--text"
              @click="cancelLease(item.leaseId)">Cancel</v-btn>
        </template>
        <template v-slot:header.dogId="{ header }">
          <v-text-field hide-details
            v-model="searchParams.dogId"
            label="Dog ID"
            clearable
            @click:clear="searchParams.dogId = ''"
            v-on:keyup.enter="applyFilter"
          />&nbsp;
          <v-text-field hide-details
            class="mt-0 pt-0 mb-2"
            v-model="searchParams.dogName"
            label="Dog Name"
            clearable
            @click:clear="searchParams.dogName = ''"
            v-on:keyup.enter="applyFilter"
          />
        </template>
        <template v-slot:header.lessees="{ header }">
          <v-text-field hide-details
            v-model="searchParams.lesseeName"
            label="Lessee Name/ID"
            clearable
            @click:clear="searchParams.lesseeName = ''"
            v-on:keyup.enter="applyFilter"
          />
        </template>
        <template v-slot:header.endDate>
          <div width="300px">
            <datepicker
              :date="searchParams.endDateFrom"
              label="End Date From"
              v-on:updateDates="updateDates($event, 'endDateFrom')"
              v-on:enter="updateDateSearch($event, 'endDateFrom')"
            />
            <datepicker
              :date="searchParams.endDateTo"
              label="End Date To"
              v-on:updateDates="updateDates($event, 'endDateTo')"
              v-on:enter="updateDateSearch($event, 'endDateTo')"
            />
          </div>
        </template>
        <template v-slot:header.startDate>
          <div width="300px">
              <datepicker
                :date="searchParams.startDateFrom"
                label="Start Date From"
                v-on:updateDates="updateDates($event, 'startDateFrom')"
                v-on:enter="updateDateSearch($event, 'startDateFrom')"
              />
              <datepicker
                :date="searchParams.startDateTo"
                label="Start Date To"
                v-on:updateDates="updateDates($event, 'startDateTo')"
                v-on:enter="updateDateSearch($event, 'startDateTo')"
              />
          </div>
        </template>
        <template v-slot:header.registrationDate>
          <div width="300px">
            <datepicker
                :date="searchParams.registrationDateFrom"
                label="Registration Date From"
                v-on:updateDates="updateDates($event, 'registrationDateFrom')"
                v-on:enter="updateDateSearch($event, 'registrationDateFrom')"
              />
            <datepicker
              :date="searchParams.registrationDateTo"
              label="Registration Date To"
              v-on:updateDates="updateDates($event, 'registrationDateTo')"
              v-on:enter="updateDateSearch($event, 'registrationDateTo')"
            />
          </div>
        </template>
    </v-data-table>
  </v-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import CoownersList, { OwnershipType } from "@/components/common/CoownersList.vue";
import Datepicker from "@/components/common/inputs/DatepickerFormatted.vue";
import PaginationSelect from "@/components/common/PaginationSelect.vue";

import leaseService from "@/services/admin/leaseService";
import { getErrorMessage } from "@/services/errorHandler";

import { MetaNames } from "@/constants";
import { UserInfo } from "@/types/User";
import { PaginationOptions, PaginatedResponse } from "@/types/Pagination";

export default Vue.extend({
  name: "LeaseList",
  components: {
    CoownersList,
    Datepicker,
    PaginationSelect
  },
  data: function () {
    return {
      title: MetaNames.AdminLeases,
      loading: false,
      isError: false,
      errorMessage: "",
      paginatedResponse: {} as PaginatedResponse<object>,
      searchParams: {
        dogId: "",
        dogName: "",
        lesseeName: "",
        registrationDateFrom: "",
        registrationDateTo: "",
        startDateFrom: "",
        startDateTo: "",
        endDateFrom: "",
        endDateTo: "",
      },
      options: {} as PaginationOptions,
      headers: [
        { text: "#ID", align: "left", value: "leaseId" },
        { text: "Dog ID", align: "left", value: "dogId" },
        {
          text: "Registration date",
          align: "left",
          value: "registrationDate",
          width: "160px",
        },
        {
          text: "Start Date",
          align: "left",
          value: "startDate",
          width: "160px",
        },
        { text: "End Date", align: "left", value: "endDate"},
        { text: "Lessees", align: "left", value: "lessees", },
        {
          text: "Cancelled?",
          align: "left",
          value: "isCancelled",
          width: "50px",
        },        
        { text: "", align: "left", value: "actionButton", sortable:false },
      ],
      footerProps: { 'items-per-page-options': [10, 20, 30, 40, 50, 100] },
    };
  },
  created: function () {
    this.load();
  },
  watch: {
    options: {
      handler() {
        this.load();
      },
      deep: true,
    }
  },
  methods: {
    createNewLease: function () {
      this.$router.push({ path: '/admin/leases' });
    },
    clearAllFilters: function () {
      this.options.page = 1;
      this.options.itemsPerPage = 10;
      this.searchParams = {
        dogId: "",
        dogName: "",
        lesseeName: "",
        registrationDateFrom: "",
        registrationDateTo: "",
        startDateFrom: "",
        startDateTo: "",
        endDateFrom: "",
        endDateTo: "",
      };
    },
    showRegistration: function (id: any, _: any) {
      this.$router.push({ path: `/admin/registration/lease/${id}` });
    },
    cancelLease: function (id: any, _: any) {
      this.loading = true;
      leaseService
        .cancelLease(id)
        .then((_) => {
          this.load();
        })
        .catch(this.$handleError);
    },
    load: async function () {
      this.loading = true;

      const {
        page,
        itemsPerPage,
      } = this.options;

      try {
        const response = await leaseService.getLeasesList({
          ...this.options,
          page: (page ?? 1) - 1,
          itemsPerPage: itemsPerPage ?? 10
        }, this.searchParams);

        this.loading = false;
        this.paginatedResponse = response;
      } catch (ex: any) {
        this.loading = false;
        this.isError = true;

        this.errorMessage = getErrorMessage(ex);
      }
    },
    applyFilter() {
      this.options.page = 1;
      this.options.itemsPerPage = 10;

      this.load();
    },
    updateDates: function (val: any, key: string) {
      (this as any).searchParams[key] = val.date;
    },
    updateDateSearch: async function(val: any, key: string) {
      await this.updateDates(val, key);
      this.load();
    },
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
    dogOwnershipType: function () {
      return OwnershipType.Dog;
    },
    leaseOwnershipType: function () {
      return OwnershipType.Lease;
    },
  },
});
</script>
